import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Input, InputNumber, Modal, message, Select, Space, Tooltip, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';

import StandardTable from '../../components/common/StandardTable';
import { fetchDccOldArchiveSearch, fetchDccOldArchiveSearchStreaming, fetchDccOldArchiveTypes } from '../../services/api';
import type { DccOldArchive } from '../../types';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DateRange = [Dayjs | null, Dayjs | null] | null;

type DccOldArchiveLoose = DccOldArchive & {
  id?: any;
  File_Name?: any;
  file_Name?: any;
  Path?: any;
  Type?: any;
  Size?: any;
  created_at?: any;
  updated_at?: any;
};

 type DccOldArchiveFilterState = {
  file_name_q: string;
  path_q: string;
  type: string[];
  created_from?: string;
  created_to?: string;
  modified_from?: string;
  modified_to?: string;
 };

 const DccOldArchiveFiles: React.FC = () => {
  const navigate = useNavigate();

  const [fileNameFilter, setFileNameFilter] = useState('');
  const [debouncedFileName, setDebouncedFileName] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [debouncedPath, setDebouncedPath] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [typeSearchText, setTypeSearchText] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);

  const [dateCreatedRange, setDateCreatedRange] = useState<DateRange>(null);
  const [dateModifiedRange, setDateModifiedRange] = useState<DateRange>(null);

  const [archiveRootHandle, setArchiveRootHandle] = useState<any>(null);
  const [preview, setPreview] = useState<{ url: string | null; x: number; y: number }>({ url: null, x: 0, y: 0 });
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [thumbSize, setThumbSize] = useState<number>(200);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState<boolean>(false);
  const [destinationHandle, setDestinationHandle] = useState<any>(null);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [currentCopyName, setCurrentCopyName] = useState<string | null>(null);

  // Server-side batch paging
  const [serverLimit, setServerLimit] = useState<number>(1000);
  const [serverOffset, setServerOffset] = useState<number>(0);

  const MAX_JSON_LIMIT = 20000;
  const useStreaming = serverLimit > MAX_JSON_LIMIT;

  const offset = useMemo(() => (serverOffset < 0 ? 0 : serverOffset), [serverOffset]);

  // Client-side table pagination (within the fetched batch)
  const [tablePageSize, setTablePageSize] = useState<number>(50);
  const [tableCurrentPage, setTableCurrentPage] = useState<number>(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFileName(fileNameFilter), 300);
    return () => clearTimeout(t);
  }, [fileNameFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPath(pathFilter), 300);
    return () => clearTimeout(t);
  }, [pathFilter]);

  const normalize = useCallback((v: any) => (v == null ? '' : String(v)).trim(), []);

  const parseSearchTerms = useCallback((q: any): string[] => {
    const s = normalize(q);
    if (!s) return [];
    const out: string[] = [];
    try {
      const re = /"([^"]+)"|(\S+)/g;
      let m: RegExpExecArray | null;
      // eslint-disable-next-line no-cond-assign
      while ((m = re.exec(s)) !== null) {
        const phrase = m[1];
        const token = m[2];
        const val = normalize(phrase || token);
        if (val) out.push(val.toLowerCase());
      }
    } catch {
      out.push(s.toLowerCase());
    }
    return out;
  }, [normalize]);

  const createdFrom = useMemo(() => {
    const start = dateCreatedRange?.[0];
    return start ? start.startOf('day').toISOString() : undefined;
  }, [dateCreatedRange]);

  const createdTo = useMemo(() => {
    const end = dateCreatedRange?.[1];
    return end ? end.endOf('day').toISOString() : undefined;
  }, [dateCreatedRange]);

  const modifiedFrom = useMemo(() => {
    const start = dateModifiedRange?.[0];
    return start ? start.startOf('day').toISOString() : undefined;
  }, [dateModifiedRange]);

  const modifiedTo = useMemo(() => {
    const end = dateModifiedRange?.[1];
    return end ? end.endOf('day').toISOString() : undefined;
  }, [dateModifiedRange]);

  const uiFilters = useMemo((): DccOldArchiveFilterState => {
    return {
      file_name_q: normalize(debouncedFileName),
      path_q: normalize(debouncedPath),
      type: (typeFilter || []).map(t => normalize(t)).filter(Boolean),
      created_from: createdFrom,
      created_to: createdTo,
      modified_from: modifiedFrom,
      modified_to: modifiedTo,
    };
  }, [createdFrom, createdTo, debouncedFileName, debouncedPath, modifiedFrom, modifiedTo, typeFilter]);

  const [serverFilters, setServerFilters] = useState<DccOldArchiveFilterState>({
    file_name_q: '',
    path_q: '',
    type: [],
    created_from: undefined,
    created_to: undefined,
    modified_from: undefined,
    modified_to: undefined,
  });

  const [baselineRows, setBaselineRows] = useState<DccOldArchive[]>([]);
  const [baselineIsComplete, setBaselineIsComplete] = useState<boolean>(false);

  const hasAnyUiFilter = useMemo(() => {
    return (
      uiFilters.file_name_q.length > 0 ||
      uiFilters.path_q.length > 0 ||
      (uiFilters.type?.length || 0) > 0 ||
      !!uiFilters.created_from ||
      !!uiFilters.created_to ||
      !!uiFilters.modified_from ||
      !!uiFilters.modified_to
    );
  }, [uiFilters]);

  const hasAnyServerFilter = useMemo(() => {
    return (
      normalize(serverFilters.file_name_q).length > 0 ||
      normalize(serverFilters.path_q).length > 0 ||
      (serverFilters.type?.length || 0) > 0 ||
      !!serverFilters.created_from ||
      !!serverFilters.created_to ||
      !!serverFilters.modified_from ||
      !!serverFilters.modified_to
    );
  }, [serverFilters]);

  const canNarrowText = useCallback((baseline: string, next: string) => {
    const bTerms = parseSearchTerms(baseline);
    const nTerms = parseSearchTerms(next);
    if (bTerms.length === 0) return false;
    if (nTerms.length === 0) return false;
    // Narrowing if next contains everything baseline had (and possibly more)
    return bTerms.every(t => nTerms.includes(t));
  }, [parseSearchTerms]);

  const canNarrowType = useCallback((baseline: string[], next: string[]) => {
    const b = (baseline || []).map(v => normalize(v).toLowerCase()).filter(Boolean);
    const n = (next || []).map(v => normalize(v).toLowerCase()).filter(Boolean);
    if (n.length === 0) return false;
    if (b.length === 0) return false;
    const bset = new Set(b);
    return n.every(v => bset.has(v));
  }, [normalize]);

  const canNarrowDateRange = useCallback((baselineFrom?: string, baselineTo?: string, nextFrom?: string, nextTo?: string) => {
    const bFrom = baselineFrom ? Date.parse(baselineFrom) : null;
    const bTo = baselineTo ? Date.parse(baselineTo) : null;
    const nFrom = nextFrom ? Date.parse(nextFrom) : null;
    const nTo = nextTo ? Date.parse(nextTo) : null;

    if (nFrom == null && nTo == null) return false;
    if (bFrom == null && bTo == null) return false;
    if (nFrom != null && bFrom != null && nFrom < bFrom) return false;
    if (nTo != null && bTo != null && nTo > bTo) return false;
    return true;
  }, []);

  useEffect(() => {
    if (!preview.url) return undefined;
    const url = preview.url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [preview.url]);

  const canFilterLocally = useCallback(
    (baseline: DccOldArchiveFilterState, next: DccOldArchiveFilterState) => {
      if (!baselineIsComplete) return false;
      if (offset !== 0) return false;

      const fileOk =
        normalize(next.file_name_q).length === 0
          ? false
          : normalize(baseline.file_name_q).length === 0
            ? false
            : canNarrowText(baseline.file_name_q, next.file_name_q);

      const pathOk =
        normalize(next.path_q).length === 0
          ? false
          : normalize(baseline.path_q).length === 0
            ? false
            : canNarrowText(baseline.path_q, next.path_q);

      const typeOk =
        (next.type?.length || 0) === 0
          ? false
          : (baseline.type?.length || 0) === 0
            ? false
            : canNarrowType(baseline.type, next.type);

      const createdOk = canNarrowDateRange(
        baseline.created_from,
        baseline.created_to,
        next.created_from,
        next.created_to
      );
      const modifiedOk = canNarrowDateRange(
        baseline.modified_from,
        baseline.modified_to,
        next.modified_from,
        next.modified_to
      );

      const fileSame = normalize(next.file_name_q).toLowerCase() === normalize(baseline.file_name_q).toLowerCase();
      const pathSame = normalize(next.path_q).toLowerCase() === normalize(baseline.path_q).toLowerCase();
      const typeSame =
        (baseline.type || []).map(v => normalize(v).toLowerCase()).sort().join('|') ===
        (next.type || []).map(v => normalize(v).toLowerCase()).sort().join('|');
      const createdSame = baseline.created_from === next.created_from && baseline.created_to === next.created_to;
      const modifiedSame = baseline.modified_from === next.modified_from && baseline.modified_to === next.modified_to;

      const allSame = fileSame && pathSame && typeSame && createdSame && modifiedSame;
      if (allSame) return true;

      const isNarrowOrSame = (fieldSame: boolean, fieldOk: boolean) => fieldSame || fieldOk;

      return (
        isNarrowOrSame(fileSame, fileOk) &&
        isNarrowOrSame(pathSame, pathOk) &&
        isNarrowOrSame(typeSame, typeOk) &&
        isNarrowOrSame(createdSame, createdOk) &&
        isNarrowOrSame(modifiedSame, modifiedOk)
      );
    },
    [baselineIsComplete, canNarrowDateRange, canNarrowText, canNarrowType, offset]
  );

  const applyLocalFilter = useCallback((input: DccOldArchive[], f: DccOldArchiveFilterState) => {
    const fnTerms = parseSearchTerms(f.file_name_q);
    const pTerms = parseSearchTerms(f.path_q);
    const typeSet = new Set((f.type || []).map(v => normalize(v).toLowerCase()).filter(Boolean));
    const cFrom = f.created_from ? Date.parse(f.created_from) : null;
    const cTo = f.created_to ? Date.parse(f.created_to) : null;
    const mFrom = f.modified_from ? Date.parse(f.modified_from) : null;
    const mTo = f.modified_to ? Date.parse(f.modified_to) : null;

    return (input || []).filter(r => {
      if (fnTerms.length > 0) {
        const val = normalize((r as any).file_name).toLowerCase();
        if (!fnTerms.every(t => val.includes(t))) return false;
      }
      if (pTerms.length > 0) {
        const val = normalize((r as any).path).toLowerCase();
        if (!pTerms.every(t => val.includes(t))) return false;
      }
      if (typeSet.size > 0) {
        const val = normalize((r as any).type).toLowerCase();
        if (!typeSet.has(val)) return false;
      }
      if (cFrom != null || cTo != null) {
        const raw = (r as any).date_created;
        const ts = raw ? Date.parse(String(raw)) : NaN;
        if (!isFinite(ts)) return false;
        if (cFrom != null && ts < cFrom) return false;
        if (cTo != null && ts > cTo) return false;
      }
      if (mFrom != null || mTo != null) {
        const raw = (r as any).date_modified;
        const ts = raw ? Date.parse(String(raw)) : NaN;
        if (!isFinite(ts)) return false;
        if (mFrom != null && ts < mFrom) return false;
        if (mTo != null && ts > mTo) return false;
      }
      return true;
    });
  }, [normalize, parseSearchTerms]);

  const hasAnyFilter = hasAnyServerFilter;

  const typeSearchQuery = useQuery({
    queryKey: [
      'dcc_old_archive_types',
      normalize(typeSearchText),
      uiFilters.file_name_q,
      uiFilters.path_q,
      uiFilters.created_from,
      uiFilters.created_to,
      uiFilters.modified_from,
      uiFilters.modified_to,
    ],
    queryFn: () =>
      fetchDccOldArchiveTypes({
        q: normalize(typeSearchText) || undefined,
        limit: 200,
        file_name_q: uiFilters.file_name_q || undefined,
        path_q: uiFilters.path_q || undefined,
        created_from: uiFilters.created_from,
        created_to: uiFilters.created_to,
        modified_from: uiFilters.modified_from,
        modified_to: uiFilters.modified_to,
      }),
    enabled: typeDropdownOpen,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const typeNotFoundContent = useMemo(() => {
    if (typeSearchQuery.isFetching) return 'Loading...';
    if (typeSearchQuery.isError) {
      const err: any = typeSearchQuery.error;
      const status = err?.response?.status;
      const msg = String(err?.response?.data?.error || err?.message || err || 'Error');
      return status ? `Error (${status}): ${msg}` : `Error: ${msg}`;
    }
    return 'No types found';
  }, [typeSearchQuery.error, typeSearchQuery.isError, typeSearchQuery.isFetching]);

  const searchQuery = useQuery({
    queryKey: [
      'dcc_old_archive_search',
      serverFilters.file_name_q,
      serverFilters.path_q,
      serverFilters.type,
      serverFilters.created_from,
      serverFilters.created_to,
      serverFilters.modified_from,
      serverFilters.modified_to,
      serverLimit,
      offset,
    ],
    enabled: hasAnyFilter && !useStreaming,
    queryFn: () =>
      fetchDccOldArchiveSearch({
        file_name_q: normalize(serverFilters.file_name_q) || undefined,
        path_q: normalize(serverFilters.path_q) || undefined,
        type: (serverFilters.type || []).map(t => normalize(t)).filter(Boolean),
        created_from: serverFilters.created_from,
        created_to: serverFilters.created_to,
        modified_from: serverFilters.modified_from,
        modified_to: serverFilters.modified_to,
        order_by: 'file_name ASC, path ASC',
        limit: serverLimit,
        offset: offset,
      }),
    staleTime: 15 * 1000,
  });

  const [streamError, setStreamError] = useState<any>(null);
  const [streamIsLoading, setStreamIsLoading] = useState<boolean>(false);
  const [streamIsDone, setStreamIsDone] = useState<boolean>(false);
  const [streamTrigger, setStreamTrigger] = useState<number>(0);

  useEffect(() => {
    if (!hasAnyFilter || !useStreaming) {
      setStreamError(null);
      setStreamIsLoading(false);
      setStreamIsDone(false);
      return;
    }

    setStreamError(null);
    setStreamIsLoading(true);
    setStreamIsDone(false);
    setServerOffset(0);
    setTableCurrentPage(1);
    setBaselineRows([]);
    setBaselineIsComplete(false);
    setDisplayRows([]);

    const ctrl = new AbortController();

    const appendMapped = (rawRows: any[]) => {
      setBaselineRows(prev => {
        const base = prev.length;
        const mapped = (rawRows || []).map((r, i) => {
          const id = (r as any).id;
          const file_name = (r as any).file_name ?? (r as any).file_Name ?? (r as any).File_Name ?? '';
          const path = (r as any).path ?? (r as any).Path ?? null;
          const type = (r as any).type ?? (r as any).Type ?? null;
          const size = (r as any).size ?? (r as any).Size ?? null;
          const date_created = (r as any).date_created ?? (r as any).created_at ?? null;
          const date_modified = (r as any).date_modified ?? (r as any).updated_at ?? null;
          return {
            id: (typeof id === 'number' && isFinite(id)) ? id : -(base + i + 1),
            file_name,
            path,
            type,
            size,
            date_created,
            date_modified,
          } as any;
        });
        return prev.concat(mapped);
      });

      setDisplayRows(prev => {
        const base = prev.length;
        const mapped = (rawRows || []).map((r, i) => {
          const id = (r as any).id;
          const file_name = (r as any).file_name ?? (r as any).file_Name ?? (r as any).File_Name ?? '';
          const path = (r as any).path ?? (r as any).Path ?? null;
          const type = (r as any).type ?? (r as any).Type ?? null;
          const size = (r as any).size ?? (r as any).Size ?? null;
          const date_created = (r as any).date_created ?? (r as any).created_at ?? null;
          const date_modified = (r as any).date_modified ?? (r as any).updated_at ?? null;
          return {
            id: (typeof id === 'number' && isFinite(id)) ? id : -(base + i + 1),
            file_name,
            path,
            type,
            size,
            date_created,
            date_modified,
          } as any;
        });
        return prev.concat(mapped);
      });
    };

    const stream = fetchDccOldArchiveSearchStreaming({
      params: {
        file_name_q: normalize(serverFilters.file_name_q) || undefined,
        path_q: normalize(serverFilters.path_q) || undefined,
        type: (serverFilters.type || []).map(t => normalize(t)).filter(Boolean),
        created_from: serverFilters.created_from,
        created_to: serverFilters.created_to,
        modified_from: serverFilters.modified_from,
        modified_to: serverFilters.modified_to,
        order_by: 'file_name ASC, path ASC',
        limit: serverLimit,
        offset: 0,
      },
      onBatch: rows2 => appendMapped(rows2 as any[]),
      onDone: () => {
        setStreamIsLoading(false);
        setStreamIsDone(true);
        setBaselineIsComplete(true);
      },
      onError: err => {
        setStreamIsLoading(false);
        setStreamIsDone(false);
        setStreamError(err);
      },
      signal: ctrl.signal,
    });

    return () => {
      try {
        ctrl.abort();
      } catch {}
      try {
        stream.abort();
      } catch {}
    };
  }, [
    hasAnyFilter,
    normalize,
    serverFilters.created_from,
    serverFilters.created_to,
    serverFilters.file_name_q,
    serverFilters.modified_from,
    serverFilters.modified_to,
    serverFilters.path_q,
    serverFilters.type,
    serverLimit,
    streamTrigger,
    useStreaming,
  ]);

  const mapRows = useCallback((rawRows: any[] | undefined | null) => {
    const raw = (rawRows || []) as DccOldArchiveLoose[];
    return (raw || []).map((r, _i) => {
      const id = (r as any).id;
      const file_name = (r as any).file_name ?? (r as any).file_Name ?? (r as any).File_Name ?? '';
      const path = (r as any).path ?? (r as any).Path ?? null;
      const type = (r as any).type ?? (r as any).Type ?? null;
      const size = (r as any).size ?? (r as any).Size ?? null;
      const date_created = (r as any).date_created ?? (r as any).created_at ?? null;
      const date_modified = (r as any).date_modified ?? (r as any).updated_at ?? null;

      return {
        // Prefer DB primary key. If missing (e.g. older rows not backfilled yet), derive a stable unique fallback.
        id: (typeof id === 'number' && isFinite(id)) ? id : -(offset + _i + 1),
        file_name,
        path,
        type,
        size,
        date_created,
        date_modified,
      } as any;
    }) as any[];
  }, [offset]);

  const [displayRows, setDisplayRows] = useState<DccOldArchive[]>([]);

  useEffect(() => {
    if (!hasAnyFilter) {
      setBaselineRows([]);
      setBaselineIsComplete(false);
      setDisplayRows([]);
      return;
    }
    if (searchQuery.isFetching) return;
    if (searchQuery.isError) return;
    if (!searchQuery.data) return;

    const mapped = mapRows((searchQuery.data as any)?.rows);
    const complete = offset === 0 && !((searchQuery.data as any)?.has_more);
    setBaselineRows(mapped);
    setBaselineIsComplete(!!complete);
    setDisplayRows(mapped);
  }, [hasAnyFilter, mapRows, offset, searchQuery.data, searchQuery.isError, searchQuery.isFetching]);

  useEffect(() => {
    if (!hasAnyUiFilter) {
      setServerFilters({
        file_name_q: '',
        path_q: '',
        type: [],
        created_from: undefined,
        created_to: undefined,
        modified_from: undefined,
        modified_to: undefined,
      });
      setServerOffset(0);
      setTableCurrentPage(1);
      setBaselineRows([]);
      setBaselineIsComplete(false);
      setDisplayRows([]);
      return;
    }

    if (!hasAnyServerFilter) {
      setServerFilters(uiFilters);
      setServerOffset(0);
      setTableCurrentPage(1);
      return;
    }

    if (canFilterLocally(serverFilters, uiFilters)) {
      setDisplayRows(applyLocalFilter(baselineRows, uiFilters));
      setTableCurrentPage(1);
      return;
    }

    const fileSame =
      normalize(uiFilters.file_name_q).toLowerCase() === normalize(serverFilters.file_name_q).toLowerCase();
    const pathSame = normalize(uiFilters.path_q).toLowerCase() === normalize(serverFilters.path_q).toLowerCase();
    const typeSame =
      (serverFilters.type || []).map(v => normalize(v).toLowerCase()).sort().join('|') ===
      (uiFilters.type || []).map(v => normalize(v).toLowerCase()).sort().join('|');
    const createdSame =
      serverFilters.created_from === uiFilters.created_from && serverFilters.created_to === uiFilters.created_to;
    const modifiedSame =
      serverFilters.modified_from === uiFilters.modified_from && serverFilters.modified_to === uiFilters.modified_to;

    if (fileSame && pathSame && typeSame && createdSame && modifiedSame) {
      // UI filters already match the current server query; no need to update serverFilters.
      return;
    }

    setServerFilters(uiFilters);
    setServerOffset(0);
    setTableCurrentPage(1);
  }, [
    applyLocalFilter,
    baselineRows,
    canFilterLocally,
    hasAnyServerFilter,
    hasAnyUiFilter,
    normalize,
    serverFilters,
    uiFilters,
  ]);

  const rows = displayRows;

  const typeOptions = useMemo(() => {
    try {
      const map = new Map<string, string>();

      // Prefer server-backed typeahead options
      const serverTypes = ((typeSearchQuery.data as any)?.types || []) as string[];
      serverTypes.forEach(t => {
        const raw = normalize(t);
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!map.has(key)) map.set(key, raw);
      });

      // Always include currently selected types so they don't disappear from the dropdown
      (typeFilter || []).forEach(t => {
        const raw = normalize(t);
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!map.has(key)) map.set(key, raw);
      });

      // Fallback: include types present in currently loaded rows
      (rows || []).forEach(r => {
        const raw = normalize((r as any).type);
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!map.has(key)) map.set(key, raw);
      });

      return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
    } catch {
      return [] as string[];
    }
  }, [normalize, rows, typeFilter, typeSearchQuery.data]);

  const isLoading = useStreaming ? streamIsLoading : searchQuery.isFetching;
  const isError = useStreaming ? !!streamError : searchQuery.isError;
  const error = (useStreaming ? streamError : (searchQuery.error as any)) as any;
  const hasMore = useStreaming ? false : !!searchQuery.data?.has_more;

  const tooltipProps = useMemo(
    () => ({
      placement: 'topLeft' as const,
      overlayStyle: { zIndex: 2000, maxWidth: 700, wordBreak: 'break-word' as const },
      getPopupContainer: () => document.body,
      trigger: ['hover', 'focus'] as any,
      autoAdjustOverflow: true,
      mouseEnterDelay: 0.1,
    }),
    []
  );

  const formatDateDisplay = useCallback((v: any) => {
    if (v == null) return '';
    const s = String(v);
    // RFC1123 example: "Sat, 04 Aug 2018 09:48:00 GMT" -> "04 Aug 2018 09:48"
    if (/^[A-Za-z]{3},\s\d{2}\s[A-Za-z]{3}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT$/.test(s)) {
      return s.substring(5, 5 + 17);
    }
    // ISO example: "2018-08-04T09:48:00Z" -> "2018-08-04 09:48"
    if (s.includes('T') && s.length >= 16) {
      return s.slice(0, 16).replace('T', ' ');
    }
    return s;
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'File Name',
        dataIndex: 'file_name',
        key: 'file_name',
        width: 240,
        render: (text: string) => (
          <Tooltip {...tooltipProps} title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Path',
        dataIndex: 'path',
        key: 'path',
        width: 420,
        render: (text: string) => (
          <Tooltip {...tooltipProps} title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (text: string) => (
          <Tooltip {...tooltipProps} title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        width: 100,
        render: (v: any) => (v == null ? '' : String(v)),
      },
      {
        title: 'Date Created',
        dataIndex: 'date_created',
        key: 'date_created',
        width: 120,
        render: (v: any) => {
          if (v == null) return '';
          const full = String(v);
          const short = formatDateDisplay(v);
          return (
            <Tooltip {...tooltipProps} title={full}>
              <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{short}</span>
            </Tooltip>
          );
        },
      },
      {
        title: 'Date Modified',
        dataIndex: 'date_modified',
        key: 'date_modified',
        width: 120,
        render: (v: any) => {
          if (v == null) return '';
          const full = String(v);
          const short = formatDateDisplay(v);
          return (
            <Tooltip {...tooltipProps} title={full}>
              <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{short}</span>
            </Tooltip>
          );
        },
      },
    ],
    [tooltipProps, formatDateDisplay]
  );

  const handleTableChange = (pagination: any) => {
    const nextCurrent = pagination?.current;
    const nextSize = pagination?.pageSize;
    if (typeof nextCurrent === 'number' && isFinite(nextCurrent) && nextCurrent > 0) {
      setTableCurrentPage(nextCurrent);
    }
    if (typeof nextSize === 'number' && isFinite(nextSize) && nextSize > 0) {
      setTablePageSize(nextSize);
    }
  };

  // When filters change (debounced), reset both server batch and table page
  useEffect(() => {
    setServerOffset(0);
    setTableCurrentPage(1);
  }, [debouncedFileName, debouncedPath, typeFilter, createdFrom, createdTo, modifiedFrom, modifiedTo]);

  const goPrevBatch = () => {
    if (useStreaming) return;
    setServerOffset(prev => Math.max(0, prev - serverLimit));
    setTableCurrentPage(1);
  };

  const goNextBatch = () => {
    if (useStreaming) return;
    if (!hasMore) return;
    setServerOffset(prev => prev + serverLimit);
    setTableCurrentPage(1);
  };

  const resetFilters = () => {
    setFileNameFilter('');
    setPathFilter('');
    setTypeFilter([]);
    setDateCreatedRange(null);
    setDateModifiedRange(null);
    setServerOffset(0);
    setTableCurrentPage(1);
  };

  const isFileSystemApiAvailable = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'showDirectoryPicker' in window;
  }, []);

  const selectDestinationFolder = useCallback(async () => {
    if (!isFileSystemApiAvailable) {
      message.warning('File System Access API is not available in this browser.');
      return;
    }

    try {
      const handle = await (window as any).showDirectoryPicker();
      if (!handle) return;

      if (typeof handle.requestPermission === 'function') {
        try {
          await handle.requestPermission({ mode: 'readwrite' });
        } catch {}
      }

      setDestinationHandle(handle);
      message.success('Destination folder selected.');
    } catch (err: any) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      message.error('Failed to select destination folder.');
    }
  }, [isFileSystemApiAvailable]);

  const selectArchiveRoot = useCallback(async () => {
    if (!isFileSystemApiAvailable) {
      message.warning('File System Access API is not available in this browser.');
      return;
    }
    try {
      const handle = await (window as any).showDirectoryPicker();
      if (!handle) return;
      if (typeof handle.requestPermission === 'function') {
        try {
          await handle.requestPermission({ mode: 'read' });
        } catch {}
      }
      setArchiveRootHandle(handle);
      message.success('Archive root folder selected.');
    } catch (err: any) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      message.error('Failed to select archive root folder.');
    }
  }, [isFileSystemApiAvailable]);

  const copySelectedToDestination = useCallback(async () => {
    if (!isFileSystemApiAvailable) {
      message.warning('File System Access API is not available in this browser.');
      return;
    }
    if (!archiveRootHandle) {
      message.warning('Archive root folder is not selected.');
      return;
    }
    if (!destinationHandle) {
      message.warning('Destination folder is not selected.');
      return;
    }
    if (!selectedRowIds.length) {
      message.warning('No rows selected to copy.');
      return;
    }

    setIsCopying(true);
    setCurrentCopyName(null);
    try {
      const rowsById = new Map<string, any>();
      (rows || []).forEach((r: any) => {
        const id = String(r?.id ?? '');
        if (id) rowsById.set(id, r);
      });

      let successCount = 0;
      let failureCount = 0;

      const getUniqueDestFileHandle = async (baseName: string): Promise<any> => {
        const dotIndex = baseName.lastIndexOf('.');
        const hasExt = dotIndex > 0;
        const namePart = hasExt ? baseName.slice(0, dotIndex) : baseName;
        const extPart = hasExt ? baseName.slice(dotIndex) : '';

        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const candidate = attempt === 0 ? baseName : `${namePart} (${attempt})${extPart}`;
          try {
            // eslint-disable-next-line no-await-in-loop
            await destinationHandle.getFileHandle(candidate, { create: false });
          } catch {
            // eslint-disable-next-line no-await-in-loop
            return destinationHandle.getFileHandle(candidate, { create: true });
          }
          attempt += 1;
        }
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const id of selectedRowIds) {
        const row = rowsById.get(String(id));
        if (!row) {
          // eslint-disable-next-line no-continue
          failureCount += 1;
          // eslint-disable-next-line no-continue
          continue;
        }

        const rawPath = (row as any).path;
        const normalizedFull = rawPath ? String(rawPath).trim() : '';
        if (!normalizedFull) {
          // eslint-disable-next-line no-continue
          failureCount += 1;
          // eslint-disable-next-line no-continue
          continue;
        }

        let rel = normalizedFull.replace(/^[A-Za-z]:/, '');
        rel = rel.replace(/^\\+/, '').replace(/^\/+/, '');

        const segments = rel.split(/[\\/]+/).filter(Boolean);
        if (!segments.length) {
          // eslint-disable-next-line no-continue
          failureCount += 1;
          // eslint-disable-next-line no-continue
          continue;
        }

        try {
          let current: any = archiveRootHandle;
          for (let i = 0; i < segments.length - 1; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            current = await current.getDirectoryHandle(segments[i], { create: false });
          }

          const leafName = segments[segments.length - 1];
          // eslint-disable-next-line no-await-in-loop
          const srcFileHandle = await current.getFileHandle(leafName, { create: false });
          // eslint-disable-next-line no-await-in-loop
          const srcFile = await srcFileHandle.getFile();
          setCurrentCopyName(srcFile.name);

          // eslint-disable-next-line no-await-in-loop
          const destFileHandle = await getUniqueDestFileHandle(srcFile.name);
          // eslint-disable-next-line no-await-in-loop
          const writable = await destFileHandle.createWritable();
          // eslint-disable-next-line no-await-in-loop
          await writable.write(srcFile);
          // eslint-disable-next-line no-await-in-loop
          await writable.close();

          successCount += 1;
        } catch {
          failureCount += 1;
        }
      }

      if (successCount && !failureCount) {
        message.success(`Copied ${successCount} file(s) to destination.`);
      } else if (successCount && failureCount) {
        message.warning(`Copied ${successCount} file(s), but ${failureCount} failed.`);
      } else {
        message.error('Failed to copy selected files.');
      }
    } finally {
      setIsCopying(false);
      setCurrentCopyName(null);
    }
  }, [archiveRootHandle, destinationHandle, isFileSystemApiAvailable, rows, selectedRowIds]);

  const openFileViaFileSystemApi = useCallback(
    async (fullPath: string): Promise<boolean> => {
      if (!archiveRootHandle || !isFileSystemApiAvailable) return false;

      const normalizedFull = String(fullPath).trim();
      if (!normalizedFull) return false;

      let rel = normalizedFull.replace(/^[A-Za-z]:/, '');
      rel = rel.replace(/^\\+/, '').replace(/^\/+/, '');

      const segments = rel.split(/[\\/]+/).filter(Boolean);
      if (segments.length === 0) return false;

      try {
        let current: any = archiveRootHandle;
        for (let i = 0; i < segments.length; i += 1) {
          const seg = segments[i];
          const isLast = i === segments.length - 1;
          if (isLast) {
            current = await current.getFileHandle(seg, { create: false });
          } else {
            current = await current.getDirectoryHandle(seg, { create: false });
          }
        }

        const file = await current.getFile();
        const blobUrl = URL.createObjectURL(file);
        const opened = window.open(blobUrl, '_blank');
        if (!opened) {
          message.warning('Browser blocked opening the file in a new tab.');
        }
        return true;
      } catch {
        message.warning('Could not open file via selected archive root. Falling back to path copy.');
        return false;
      }
    },
    [archiveRootHandle, isFileSystemApiAvailable]
  );

  const getFileUrlViaFileSystemApi = useCallback(
    async (fullPath: string): Promise<string | null> => {
      if (!archiveRootHandle || !isFileSystemApiAvailable) return null;

      const normalizedFull = String(fullPath).trim();
      if (!normalizedFull) return null;

      let rel = normalizedFull.replace(/^[A-Za-z]:/, '');
      rel = rel.replace(/^\\+/, '').replace(/^\/+/, '');

      const segments = rel.split(/[\\/]+/).filter(Boolean);
      if (segments.length === 0) return null;

      try {
        let current: any = archiveRootHandle;
        for (let i = 0; i < segments.length; i += 1) {
          const seg = segments[i];
          const isLast = i === segments.length - 1;
          if (isLast) {
            current = await current.getFileHandle(seg, { create: false });
          } else {
            current = await current.getDirectoryHandle(seg, { create: false });
          }
        }

        const file = await current.getFile();
        return URL.createObjectURL(file);
      } catch {
        return null;
      }
    },
    [archiveRootHandle, isFileSystemApiAvailable]
  );

  const handleRowOpenInNewTab = useCallback(
    async (record: any) => {
      const rawPath = record?.path;
      if (!rawPath) return;

      const normalized = String(rawPath).trim();
      if (!normalized) return;

      const openedViaFsApi = await openFileViaFileSystemApi(normalized);
      if (openedViaFsApi) return;

      const fileUrl = `file:///${normalized.replace(/\\/g, '/')}`;

      const opened = window.open(fileUrl, '_blank');
      if (opened) return;

      if (navigator?.clipboard?.writeText) {
        navigator.clipboard
          .writeText(normalized)
          .then(() => message.info('Local path copied to clipboard. Paste it into File Explorer.'))
          .catch(() => message.warning('Could not open file and failed to copy path.'));
      } else {
        message.info('Could not open file in browser. Copy the path from the table and open it in File Explorer.');
      }
    },
    [openFileViaFileSystemApi]
  );

  const handleRowPreview = useCallback(
    async (event: React.MouseEvent, record: any) => {
      const rawPath = record?.path;
      if (!rawPath) return;

      const normalized = String(rawPath).trim();
      if (!normalized) return;

      const url = await getFileUrlViaFileSystemApi(normalized);
      if (!url) {
        const pathToCopy = normalized;
        if (navigator?.clipboard?.writeText) {
          navigator.clipboard
            .writeText(String(pathToCopy))
            .then(() =>
              message.info(
                'Could not load preview via browser APIs. File path copied to clipboard so you can open it via Explorer.'
              )
            )
            .catch(() => message.warning('Could not load preview and failed to copy file path.'));
        } else {
          message.warning('Could not load preview for this file.');
        }
        return;
      }

      setPreview({ url, x: event.clientX + 15, y: event.clientY + 15 });
    },
    [getFileUrlViaFileSystemApi]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!rows.length) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();

      let currentIndex = rows.findIndex(r => String((r as any).id) === activeRowId);
      if (currentIndex === -1) {
        currentIndex = 0;
      }

      if (event.key === 'ArrowDown' && currentIndex < rows.length - 1) {
        currentIndex += 1;
      } else if (event.key === 'ArrowUp' && currentIndex > 0) {
        currentIndex -= 1;
      }

      const nextRow = rows[currentIndex];
      if (!nextRow) return;

      const nextId = String((nextRow as any).id);
      setActiveRowId(nextId);

      const syntheticEvent = {
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2,
      } as React.MouseEvent;
      handleRowPreview(syntheticEvent, nextRow as any);
    },
    [activeRowId, handleRowPreview, rows]
  );

  return (
    <div
      className="compact-table-container"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
          <span style={{ fontSize: 24, fontWeight: 600 }}>DCC Old Archive</span>
        </span>
      }
      style={{ margin: 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type={isError ? 'danger' : 'secondary'}>
            {!hasAnyFilter
              ? 'Set at least one filter to search.'
              : isError
                ? `Failed to load: ${String((error as any)?.message || error || '')}`
                : isLoading
                  ? 'Searching...'
                  : useStreaming
                    ? `${rows.length} rows loaded (streaming; fetch_limit=${serverLimit})${streamIsDone ? '' : ' (in progress...)'}`
                    : `${rows.length} rows loaded (fetch_limit=${serverLimit}, offset=${serverOffset})${hasMore ? ' (more available)' : (serverOffset > 0 ? ' (no more available)' : '')}`}
          </Text>
          <Space size={6}>
            <span>
              Thumbnail size:{' '}
              <InputNumber
                size="small"
                min={80}
                max={400}
                step={20}
                value={thumbSize}
                onChange={value => {
                  const num = typeof value === 'number' && isFinite(value) && value > 0 ? value : 200;
                  setThumbSize(num);
                }}
                style={{ width: 60, marginLeft: 2 }}
              />
            </span>
            {isFileSystemApiAvailable && (
              <Button size="small" onClick={selectArchiveRoot}>
                Select archive root
              </Button>
            )}
            <Button
              size="small"
              type="primary"
              disabled={!isFileSystemApiAvailable || !archiveRootHandle || !selectedRowIds.length}
              onClick={() => setIsCopyModalOpen(true)}
            >
              Copy to Local
            </Button>
            <Text type="secondary">Fetch limit:</Text>
            <InputNumber
              min={10}
              step={50}
              value={serverLimit}
              onChange={v => {
                const next = typeof v === 'number' && isFinite(v) ? v : 1000;
                setServerLimit(next);
                setServerOffset(0);
                setTableCurrentPage(1);
              }}
              style={{ width: 100 }}
              placeholder="limit"
            />
            <Button size="small" onClick={goPrevBatch} disabled={useStreaming || isLoading || !hasAnyFilter || serverOffset <= 0}>
              Prev batch
            </Button>
            <Button size="small" onClick={goNextBatch} disabled={useStreaming || isLoading || !hasAnyFilter || !hasMore}>
              Next batch
            </Button>
            <Button
              size="small"
              onClick={() => {
                if (useStreaming) {
                  setStreamTrigger(v => v + 1);
                } else {
                  searchQuery.refetch();
                }
              }}
              disabled={isLoading || !hasAnyFilter}
            >
              Refresh
            </Button>
            <Button
              size="small"
              onClick={resetFilters}
              disabled={isLoading && !hasAnyFilter}
            >
              Reset filters
            </Button>
          </Space>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'nowrap' }}>
          <Input
            placeholder='file_name'
            allowClear
            value={fileNameFilter}
            onChange={e => {
              setFileNameFilter(e.target.value);
              setTableCurrentPage(1);
            }}
            style={{ width: 220 }}
          />

          <Input
            placeholder='path (unordered AND; use "phrase")'
            allowClear
            value={pathFilter}
            onChange={e => {
              setPathFilter(e.target.value);
              setTableCurrentPage(1);
            }}
            style={{ width: 360 }}
          />

          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="type"
            value={typeFilter}
            open={typeDropdownOpen}
            onDropdownVisibleChange={(open) => {
              setTypeDropdownOpen(open);
              if (open) {
                typeSearchQuery.refetch();
              } else {
                setTypeSearchText('');
              }
            }}
            searchValue={typeSearchText}
            onSearch={v => setTypeSearchText(v || '')}
            onChange={vals => {
              setTypeFilter((vals || []) as string[]);
              setTableCurrentPage(1);
            }}
            style={{ width: 120}}
            optionFilterProp="label"
            filterOption={false}
            loading={typeSearchQuery.isFetching}
            notFoundContent={typeNotFoundContent}
            options={typeOptions.map(t => ({ value: t, label: t }))}
          />

          <RangePicker
            allowEmpty={[true, true]}
            placeholder={['created from', 'created to']}
            value={dateCreatedRange as any}
            onChange={v => {
              setDateCreatedRange((v as any) || null);
              setTableCurrentPage(1);
            }}
            style={{ width: 220 }}
          />

          <RangePicker
            allowEmpty={[true, true]}
            placeholder={['updated from', 'updated to']}
            value={dateModifiedRange as any}
            onChange={v => {
              setDateModifiedRange((v as any) || null);
              setTableCurrentPage(1);
            }}
            style={{ width: 220 }}
          />
        </div>

        <div style={{ marginTop: -6, marginBottom: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: Multi-word search uses AND logic (all words must match). Use quotes for exact phrases, e.g.{' '}
            <Text code>"abc def"</Text>.
          </Text>
        </div>

        <StandardTable
          columns={columns as any}
          dataSource={rows}
          rowKey={(record: any) => String(record?.id ?? '')}
          loading={isLoading}
          rowSelection={{
            selectedRowKeys: selectedRowIds,
            onChange: keys => setSelectedRowIds((keys || []).map(k => String(k)) as string[]),
          }}
          onRow={(record: any) => ({
            onClick: (event: React.MouseEvent) => {
              const id = String(record?.id);
              setActiveRowId(id);
              handleRowPreview(event, record);
            },
            onDoubleClick: () => handleRowOpenInNewTab(record),
          })}
          pagination={{
            current: tableCurrentPage,
            pageSize: tablePageSize,
            total: rows.length,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200', '500'],
            showTotal: (_total: number, range: [number, number]) =>
              hasAnyFilter
                ? `${range[0]}-${range[1]} of ${rows.length}`
                : '0-0',
          }}
          onChange={handleTableChange}
          size="small"
          className="compact-table zebra-table left-align-table"
          rowClassName={(record: any, index: number) => {
            const base = index % 2 === 0 ? 'zebra-row' : '';
            const isActive = activeRowId != null && String(record?.id) === activeRowId;
            if (isActive) {
              return base ? `${base} active-row` : 'active-row';
            }
            return base;
          }}
          style={{ padding: '2px' }}
          scroll={{ x: 'max-content' }}
        />
      </Space>

      <Modal
        open={isCopyModalOpen}
        title="Copy selected files to local folder"
        onCancel={() => setIsCopyModalOpen(false)}
        onOk={async () => {
          await copySelectedToDestination();
          setIsCopyModalOpen(false);
        }}
        okButtonProps={{ disabled: !destinationHandle || !selectedRowIds.length || !archiveRootHandle, loading: isCopying }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Selected files: {selectedRowIds.length}</Text>
          <Button onClick={selectDestinationFolder}>Select destination folder</Button>
          <Text type={destinationHandle ? 'secondary' : 'danger'}>
            {destinationHandle
              ? 'Destination folder selected. Confirm to copy the selected files.'
              : 'No destination folder selected yet.'}
          </Text>
          {currentCopyName && (
            <Text type="secondary">Currently copying: {currentCopyName}</Text>
          )}
        </Space>
      </Modal>

      {preview.url && (
        <div
          style={{
            position: 'fixed',
            top: preview.y,
            left: preview.x,
            pointerEvents: 'none',
            zIndex: 2000,
            border: '2px solid #ccc',
            backgroundColor: '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          }}
        >
          <img src={preview.url} alt="Preview" style={{ width: `${thumbSize}px`, display: 'block' }} />
        </div>
      )}
    </Card>
    </div>
  );
};

export default DccOldArchiveFiles;
